import { useEffect, useRef, useState } from 'react';

import { useAppSelector } from '@/configuration/setup/hooks';
import { getAssetTreeAccountIds, getAssetTreeAssetIds, getAssetTreeWorkshopIds } from '@/features/app/assetTreeSlice';
import { sendMessage, SHOW_REFRESH_PAGE_NOTIFICATION } from '@/features/app/MessageHandler';
import { useGetVehicleStatusGlobalCountersQuery } from '@/services/vehicleStatusApi';
import { removeUndefinedOrEmptyArrayAttributes } from '@/utils/commonUtils';
import useShouldSkipQuery from './useShouldSkipQuery';

const showPageRefreshNotification = (lastUpdateTime: Date | undefined) => {
    sendMessage({
        type: SHOW_REFRESH_PAGE_NOTIFICATION,
        payload: {
            lastUpdateDate: lastUpdateTime,
        },
    });
};

const useFetchVehicleStatusGlobalCounters = () => {
    const isFirstRender = useRef(true);
    const [lastUpdateTime, setLastUpdateTime] = useState<Date | undefined>();

    // get values from asset tree
    const assetIds = useAppSelector(getAssetTreeAssetIds);
    const accountIds = useAppSelector(getAssetTreeAccountIds);
    const workshopIds = useAppSelector(getAssetTreeWorkshopIds);

    const { shouldSkip, oldLocationId, newLocationId } = useShouldSkipQuery();

    const {
        data: globalCounters = {
            total_brake_linings_critical: 0,
            total_brake_linings_warning: 0,
            total_maintenance_components_almost_due: 0,
            total_maintenance_components_overdue: 0,
            total_vehicle_status_critical: 0,
            total_vehicle_status_warning: 0,
            total_vehicle_status_ok: 0,
            total_vehicle_status_unknown: 0,
            total_active_dm1_dtcs_critical: 0,
            total_active_dm1_dtcs_warning: 0,
            total_active_dm1_dtcs_other: 0,
            total_active_dm4_dtcs_critical: 0,
            total_active_dm4_dtcs_warning: 0,
            total_active_dm4_dtcs_other: 0,
        },
        isLoading,
        error,
        refetch,
        fulfilledTimeStamp,
    } = useGetVehicleStatusGlobalCountersQuery(
        removeUndefinedOrEmptyArrayAttributes({
            asset_ids: assetIds,
            account_ids: accountIds,
            workshop_ids: workshopIds,
        }),
        { skip: shouldSkip },
    );

    useEffect(() => {
        if (fulfilledTimeStamp) {
            setLastUpdateTime(new Date(fulfilledTimeStamp));
        }
    }, [fulfilledTimeStamp]);

    useEffect(() => {
        if (newLocationId !== 'VEHICLE_STATUS') {
            return;
        }

        if (oldLocationId === 'DETAIL_VIEW') {
            showPageRefreshNotification(lastUpdateTime);
            return;
        }

        if (isFirstRender.current) {
            isFirstRender.current = false;
        } else {
            refetch();
        }
    }, [newLocationId]);

    return {
        isLoading,
        hasErrors: !!error,
        globalCounters,
        triggerReload: refetch,
        lastUpdateTime,
    };
};

export default useFetchVehicleStatusGlobalCounters;
